import { useEffect, useMemo, useState } from "react";

const ItemsPanel = ({
  menus = [],
  onPlaceOrder,
  gstApplicable = false,
  gstConfig,
  isClientAvailable = true,
  isFixedMenuClient = false,
  onToggleMenu,
  onUpdateBufferCount,
  reloadMenus,
}) => {
  const [cart, setCart] = useState([]); // [{ ...menu, quantity, totalAmount }]
  const [selectedMenuID, setSelectedMenuID] = useState(null);
  const [paymentOverlay, setPaymentOverlay] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [customQty, setCustomQty] = useState("");
  const [showCustomQty, setShowCustomQty] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [bufferInputs, setBufferInputs] = useState({});
  const [confirmModal, setConfirmModal] = useState(null); // { title, message, onConfirm }

  const getMenuUniqueKey = (menu) =>
    isFixedMenuClient ? menu?.sortKey : menu?.menuID || menu?.sortKey;

  useEffect(() => {
    const next = {};
    menus.forEach((m) => {
      const key = getMenuUniqueKey(m);
      if (!key) return;
      next[key] = "";
    });
    setBufferInputs(next);
  }, [menus, isFixedMenuClient]);

  const getOrderedCount = (menu) => Number(menu?.orderedCount ?? 0);
  const getTotalCapacity = (menu) => Number(menu?.totalCapacity ?? 0);

  const shouldShowBufferControls = (menu) => {
    const ordered = getOrderedCount(menu);
    const capacity = getTotalCapacity(menu);
    if (!Number.isFinite(ordered) || !Number.isFinite(capacity)) return false;
    return ordered >= capacity;
  };

  const updateBuffer = async (menu) => {
    if (!onUpdateBufferCount) return;
    const key = getMenuUniqueKey(menu);
    const value = bufferInputs[key] ?? "";
    const parsedValue = Number(value);
    if (value === "" || !/^\d+$/.test(String(value)) || parsedValue <= 0) return;
    setConfirmModal({
      title: "Update Buffer",
      message: `Add ${parsedValue} buffer to this item?`,
      onConfirm: async () => {
        await onUpdateBufferCount(menu, parsedValue, isSoldOut(menu));
        setConfirmModal(null);
      },
    });
  };

  const gstPercent = gstConfig?.gst || 0;

  // Only show menus that have an itemNumber assigned, sorted by itemNumber
  const numberedMenus = useMemo(() => {
    const sorted = menus
      .filter((m) => m.itemNumber != null && m.itemNumber !== "")
      .sort((a, b) => Number(a.itemNumber) - Number(b.itemNumber));
    if (!searchQuery.trim()) return sorted;
    const q = searchQuery.trim().toLowerCase();
    return sorted.filter(
      (m) =>
        String(m.itemNumber).toLowerCase().includes(q) ||
        (m.itemName || "").toLowerCase().includes(q) ||
        (m.menuName || "").toLowerCase().includes(q)
    );
  }, [menus, searchQuery]);

  const getDisplayPrice = (menu) => {
    if (menu?.splitMenu && typeof menu.splitMenu === "object") {
      const prices = Object.values(menu.splitMenu)
        .map(Number)
        .filter((p) => !isNaN(p) && p > 0);
      if (prices.length > 0) return Math.min(...prices);
    }
    const directPrice = Number(menu?.itemPrice ?? menu?.price);
    if (!Number.isNaN(directPrice) && directPrice > 0) return directPrice;
    return 0;
  };

  const totalAmount = cart.reduce(
    (sum, item) => sum + (Number(item.itemPrice) || 0) * (Number(item.quantity) || 0),
    0
  );

  const gstAmount = useMemo(() => {
    if (gstPercent === 0) return 0;
    if (gstApplicable) {
      // Exclusive: GST charged on top
      return parseFloat((totalAmount * gstPercent / 100).toFixed(2));
    }
    // Inclusive: back-calculate embedded GST (display only)
    return parseFloat((totalAmount - (totalAmount / (1 + gstPercent / 100))).toFixed(2));
  }, [totalAmount, gstPercent, gstApplicable]);

  // Final payable: exclusive adds GST on top, inclusive keeps total unchanged
  const finalPayable = useMemo(() => {
    if (gstApplicable && gstPercent > 0) {
      return parseFloat((totalAmount + gstAmount).toFixed(2));
    }
    return totalAmount;
  }, [totalAmount, gstAmount, gstApplicable, gstPercent]);

  const handleItemClick = (menu) => {
    setSelectedMenuID(menu.menuID);

    // If not already in cart, add with quantity 1 as default
    setCart((prev) => {
      const exists = prev.find((c) => c.menuID === menu.menuID);
      if (exists) return prev;
      const price = getDisplayPrice(menu);
      return [
        ...prev,
        {
          ...menu,
          itemPrice: price,
          quantity: 1,
          totalAmount: price,
        },
      ];
    });
  };

  const setQuantity = (num) => {
    if (!selectedMenuID || num <= 0) return;
    setCart((prev) => {
      const idx = prev.findIndex((c) => c.menuID === selectedMenuID);
      if (idx === -1) return prev;
      const copy = [...prev];
      copy[idx] = {
        ...copy[idx],
        quantity: num,
        totalAmount: (Number(copy[idx].itemPrice) || 0) * num,
      };
      return copy;
    });
  };

  const handleNumpad = (num) => {
    if (!selectedMenuID) return;
    setQuantity(num);
  };

  const handleCustomQtySubmit = () => {
    const num = parseInt(customQty, 10);
    if (!isNaN(num) && num > 0) {
      setQuantity(num);
    }
    setCustomQty("");
    setShowCustomQty(false);
  };

  const handleRemoveItem = (menuID) => {
    setCart((prev) => prev.filter((c) => c.menuID !== menuID));
    if (selectedMenuID === menuID) setSelectedMenuID(null);
  };

  const handleSubmitClick = () => {
    if (!isClientAvailable) return;
    if (cart.length === 0 || cart.every((c) => c.quantity <= 0)) return;
    setPaymentOverlay(true);
  };

  const handlePaymentSelect = async (method) => {
    setPaymentOverlay(false);
    if (!onPlaceOrder) return;

    const validItems = cart.filter((c) => c.quantity > 0);
    if (validItems.length === 0) return;

    const paymentStatus = "Cash On Delivery";

    setSubmitting(true);
    try {
      const result = await onPlaceOrder({
        items: validItems,
        paymentStatus,
        cashPaymentMode: method, // "ONLINE" | "CASH" | "SODEXO"
        gstApplicable: Boolean(gstApplicable),
        gstAmount,
        gstPercent,
        itemTotal: totalAmount,
      });
      // Support both old boolean return and new { success, message } object
      const success = result === true || result?.success;
      if (success) {
        setCart([]);
        setSelectedMenuID(null);
        reloadMenus?.();
      } else {
        const msg = result?.message || "Order failed. Please try again.";
        setConfirmModal({
          title: result?.status === 409 ? "Items are Sold Out" : "Order Failed",
          message: msg,
          onConfirm: () => setConfirmModal(null),
          hideCancel: true,
        });
      }
    } finally {
      setSubmitting(false);
    }
  };

  const isSoldOut = (menu) => {
    if (typeof menu?.soldOut === "string") return menu.soldOut.toLowerCase() === "true";
    return Boolean(menu?.soldOut);
  };

  const hasValidCart = cart.length > 0 && cart.some((c) => c.quantity > 0);

  return (
    <div className="d-flex gap-4" style={{ height: "calc(98vh - 180px)" }}>
      {/* LEFT — Item Grid (scrollable) */}
      <div className="flex-grow-1" style={{ overflowY: "auto" }}>
        <div className="mb-3">
          <input
            type="text"
            className="form-control"
            placeholder="Search by item number or number"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{ borderRadius: 10 }}
          />
        </div>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(5, 1fr)",
            gap: 12,
          }}
        >
          {numberedMenus.map((menu) => {
            const isSelected = selectedMenuID === menu.menuID;
            const soldOut = isSoldOut(menu);
            const inCart = cart.find((c) => c.menuID === menu.menuID);

            return (
              <div
                key={menu.menuID}
                onClick={() => !soldOut && handleItemClick(menu)}
                className="card"
                style={{
                  cursor: soldOut ? "not-allowed" : "pointer",
                  border: isSelected
                    ? "2px solid #198754"
                    : inCart
                      ? "2px solid #0d6efd"
                      : "1px solid #dee2e6",
                  borderRadius: 12,
                  transition: "border 0.15s",
                }}
              >
                <div className="card-body text-center py-3 px-2">
                  <div style={{ opacity: soldOut ? 0.45 : 1, transition: "opacity 0.2s" }}>
                  <h2 className="fw-bold mb-1" style={{ fontSize: "2rem", fontWeight: 900 }}>
                    {menu.itemNumber}
                  </h2>
                  <p className="text-muted mb-2" style={{ fontSize: "0.8rem" }}>
                    ({menu.itemName || menu.menuName})
                  </p>

                  <div
                    className="d-flex align-items-center justify-content-between px-1"
                    style={{
                      backgroundColor: soldOut ? "#fff5f5" : "#f0fdf4",
                      borderRadius: 8,
                      padding: "6px 8px",
                    }}
                  >
                    <span
                      className="fw-bold"
                      style={{
                        fontSize: "0.75rem",
                        color: soldOut ? "#dc3545" : "#198754",
                        letterSpacing: 0.5,
                      }}
                    >
                      {soldOut ? "SOLD OUT" : "LIVE"}
                    </span>
                    <div
                      onClick={(e) => {
                        e.stopPropagation();
                        if (!onToggleMenu) return;
                        if (isFixedMenuClient && soldOut && getOrderedCount(menu) >= getTotalCapacity(menu)) {
                          alert("Please add a buffer count before turning this item back on.");
                          return;
                        }
                        const turning = soldOut ? "ON" : "OFF";
                        setConfirmModal({
                          title: `Turn ${turning}`,
                          message: `Are you sure you want to turn ${(menu.itemName || menu.menuName)} ${turning}?`,
                          onConfirm: () => {
                            if (isFixedMenuClient) {
                              onToggleMenu(menu, !isSoldOut(menu));
                            } else {
                              onToggleMenu(menu, !menu.soldOut);
                            }
                            setConfirmModal(null);
                          },
                        });
                      }}
                      style={{
                        width: 40,
                        height: 22,
                        borderRadius: 11,
                        backgroundColor: soldOut ? "#ccc" : "#198754",
                        position: "relative",
                        transition: "background-color 0.2s",
                        cursor: "pointer",
                        boxShadow: "inset 0 1px 3px rgba(0,0,0,0.15)",
                      }}
                    >
                      <div
                        style={{
                          width: 16,
                          height: 16,
                          borderRadius: "50%",
                          backgroundColor: "#fff",
                          position: "absolute",
                          top: 3,
                          left: soldOut ? 3 : 21,
                          transition: "left 0.2s",
                          boxShadow: "0 1px 2px rgba(0,0,0,0.2)",
                        }}
                      />
                    </div>
                  </div>
                  </div>{/* end opacity wrapper */}

                  {isFixedMenuClient && (
                    <div
                      className="d-flex justify-content-around mt-2"
                      style={{
                        backgroundColor: "#f8f9fa",
                        borderRadius: 8,
                        padding: "4px 6px",
                      }}
                    >
                      <div className="text-center">
                        <div style={{ fontSize: "0.6rem", color: "#888", textTransform: "uppercase" }}>Ordered</div>
                        <div className="fw-bold" style={{ fontSize: "0.85rem", color: "#333" }}>{getOrderedCount(menu)}</div>
                      </div>
                      <div style={{ width: 1, backgroundColor: "#dee2e6" }} />
                      <div className="text-center">
                        <div style={{ fontSize: "0.6rem", color: "#888", textTransform: "uppercase" }}>Available</div>
                        <div className="fw-bold" style={{
                          fontSize: "0.85rem",
                          color: (getTotalCapacity(menu) - getOrderedCount(menu)) <= 0 ? "#dc3545" : "#198754",
                        }}>
                          {getTotalCapacity(menu) - getOrderedCount(menu)}
                          <span style={{ fontSize: "0.6rem", color: "#999" }}>/{getTotalCapacity(menu)}</span>
                        </div>
                      </div>
                    </div>
                  )}

                  {isFixedMenuClient && shouldShowBufferControls(menu) && (
                    <div
                      className="mt-2"
                      style={{
                        backgroundColor: "#fff3cd",
                        borderRadius: 8,
                        padding: "6px 8px",
                      }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div style={{ fontSize: "0.65rem", color: "#856404", fontWeight: 600, marginBottom: 4 }}>
                        ⚠ Capacity reached — add buffer
                      </div>
                      <div className="d-flex align-items-center gap-2">
                        <input
                          type="number"
                          min={1}
                          className="form-control form-control-sm"
                          style={{ width: 56, borderRadius: 6, fontSize: "0.8rem", padding: "3px 6px", textAlign: "center" }}
                          placeholder="0"
                          value={bufferInputs[getMenuUniqueKey(menu)] ?? ""}
                          onChange={(e) => {
                            const key = getMenuUniqueKey(menu);
                            const val = e.target.value;
                            if (val === "" || /^\d+$/.test(val)) {
                              setBufferInputs((prev) => ({ ...prev, [key]: val }));
                            }
                          }}
                        />
                        <button
                          className="btn btn-sm text-white fw-semibold flex-grow-1"
                          style={{
                            fontSize: "0.7rem",
                            padding: "4px 8px",
                            borderRadius: 6,
                            backgroundColor:
                              /^\d+$/.test(String(bufferInputs[getMenuUniqueKey(menu)] ?? "")) &&
                              Number(bufferInputs[getMenuUniqueKey(menu)] ?? "") > 0
                                ? "#0d6efd" : "#adb5bd",
                          }}
                          disabled={
                            !/^\d+$/.test(String(bufferInputs[getMenuUniqueKey(menu)] ?? "")) ||
                            Number(bufferInputs[getMenuUniqueKey(menu)] ?? "") <= 0
                          }
                          onClick={(e) => {
                            e.stopPropagation();
                            updateBuffer(menu);
                          }}
                        >
                          + Add Buffer
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}

          {numberedMenus.length === 0 && (
            <div className="col-span-5 text-muted py-5 text-center">
              No items with assigned numbers found. Assign item numbers in Menus tab.
            </div>
          )}
        </div>
      </div>

      {/* RIGHT — Order Panel */}
      <div
        className="card"
        style={{
          width: 320,
          minWidth: 320,
          borderRadius: 12,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <div className="card-body d-flex flex-column p-3" style={{ overflow: "hidden", flex: 1 }}>
          <h5 className="fw-bold mb-2" style={{ flexShrink: 0 }}>Order</h5>

          {/* Entire order content scrolls together */}
          <div style={{ overflowY: "auto", flex: 1, minHeight: 0 }}>

            {/* Cart items */}
            {cart.length === 0 && (
              <p className="text-muted small">Click an item to add it to the order.</p>
            )}

            {cart.map((item) => (
              <div
                key={item.menuID}
                className="d-flex justify-content-between align-items-center mb-2 rounded"
                style={{
                  backgroundColor: selectedMenuID === item.menuID ? "#eef2ff" : "#f8f9fa",
                  border: selectedMenuID === item.menuID ? "1.5px solid #3f3d8e" : "1px solid transparent",
                  cursor: "pointer",
                  padding: "8px 10px",
                }}
                onClick={() => setSelectedMenuID(item.menuID)}
              >
                <div className="flex-grow-1" style={{ marginRight: 6 }}>
                  <span className="fw-semibold" style={{ fontSize: "0.78rem" }}>
                    {item.itemName || item.menuName}
                  </span>
                </div>
                <div className="d-flex align-items-center gap-1" style={{ flexShrink: 0 }}>
                  <span className="text-muted" style={{ fontSize: "0.7rem" }}>Qty:</span>
                  <span
                    className="fw-bold text-center"
                    style={{
                      minWidth: 26,
                      border: "1px solid #dee2e6",
                      borderRadius: 5,
                      padding: "1px 5px",
                      fontSize: "0.75rem",
                    }}
                  >
                    {item.quantity}
                  </span>
                  <span className="fw-bold" style={{ fontSize: "0.78rem" }}>
                    ₹{(Number(item.itemPrice) || 0) * (Number(item.quantity) || 0)}
                  </span>
                  <button
                    className="btn btn-sm btn-outline-danger border-0 p-0 ms-1"
                    style={{ fontSize: "0.85rem", lineHeight: 1 }}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRemoveItem(item.menuID);
                    }}
                  >
                    ×
                  </button>
                </div>
              </div>
            ))}

            {/* Numpad */}
            <div className="mt-2">
            <p className="fw-semibold mb-1 small">Set Quantity</p>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(3, 1fr)",
              gap: 4,
            }}
          >
            {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((num) => (
              <button
                key={num}
                className="btn btn-outline-secondary fw-bold"
                style={{ fontSize: "0.95rem", padding: "5px 0", borderRadius: 8 }}
                disabled={!selectedMenuID}
                onClick={() => handleNumpad(num)}
              >
                {num}
              </button>
            ))}
            <button
              className="btn btn-outline-primary fw-bold"
              style={{ fontSize: "0.8rem", padding: "5px 0", borderRadius: 8, gridColumn: "span 3" }}
              disabled={!selectedMenuID}
              onClick={() => { setShowCustomQty(true); setCustomQty(""); }}
            >
              Custom Qty (10+)
            </button>
          </div>

          {/* Custom quantity input */}
          {showCustomQty && (
            <div className="d-flex align-items-center gap-2 mt-2">
              <input
                type="number"
                min="1"
                className="form-control form-control-sm"
                style={{ width: 80, borderRadius: 8 }}
                placeholder="Qty"
                value={customQty}
                autoFocus
                onChange={(e) => setCustomQty(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") handleCustomQtySubmit(); }}
              />
              <button
                className="btn btn-sm btn-success fw-bold"
                style={{ borderRadius: 8 }}
                onClick={handleCustomQtySubmit}
              >
                Set
              </button>
              <button
                className="btn btn-sm btn-outline-secondary"
                style={{ borderRadius: 8 }}
                onClick={() => { setShowCustomQty(false); setCustomQty(""); }}
              >
                ✕
              </button>
            </div>
          )}

          {/* Bill Details + Submit */}
          </div>{/* end numpad wrapper */}
          <div className="mt-2 pt-2 border-top">
            <p className="fw-semibold small mb-1" style={{ color: "#333", fontSize: "0.75rem" }}>BILL DETAILS</p>

            {/* Item Total */}
            <div className="d-flex justify-content-between mb-1">
              <span className="small text-muted">Item Total</span>
              <span className="small fw-semibold">₹ {totalAmount}</span>
            </div>

            {/* GST Row */}
            {gstApplicable != null && (
              <div className="mb-1">
                <div className="d-flex justify-content-between align-items-center">
                  <span
                    className="small"
                    style={{ color: gstApplicable ? "#666" : "#999" }}
                  >
                    {gstApplicable
                      ? `GST (${gstPercent}%)`
                      : `GST (${gstPercent}% Inclusive)`}
                  </span>
                  <span
                    className="small"
                    style={{
                      color: gstApplicable ? "#333" : "#999",
                      fontWeight: gstApplicable ? 600 : 400,
                    }}
                  >
                    ₹ {gstAmount}
                  </span>
                </div>
                {!gstApplicable && (
                  <p className="mb-0" style={{ fontSize: "0.7rem", color: "#999" }}>
                    (Included in item prices)
                  </p>
                )}
              </div>
            )}

            {/* Divider */}
            <hr className="my-1" />

            {/* Total Payable */}
            <div className="d-flex justify-content-between align-items-center">
              <span className="fw-bold mb-0" style={{ fontSize: "0.9rem" }}>Total Payable</span>
              <span className="fw-bold mb-0" style={{ fontSize: "0.9rem" }}>₹ {finalPayable}</span>
            </div>

            {/* Submit */}
            <div className="d-flex justify-content-end mt-1">
            <button
              className={`btn btn-sm text-white fw-bold ${
                hasValidCart && !submitting ? "" : "btn-secondary"
              }`}
              style={{
                borderRadius: 8,
                padding: "6px 16px",
                fontSize: "0.85rem",
                backgroundColor: hasValidCart && !submitting && isClientAvailable ? "#3f3d8e" : undefined,
              }}
              disabled={!hasValidCart || submitting || !isClientAvailable}
              onClick={handleSubmitClick}
            >
              {submitting ? (
                <span className="d-inline-flex align-items-center gap-2">
                  <span className="spinner-border spinner-border-sm" aria-hidden="true" />
                  Submitting...
                </span>
              ) : (
                "Submit Order"
              )}
            </button>
            </div>
          </div>
          </div>{/* end scrollable wrapper */}
        </div>
      </div>
      {paymentOverlay && (
        <div
          className="position-fixed top-0 start-0 w-100 h-100 d-flex justify-content-center align-items-center"
          style={{ backgroundColor: "rgba(0,0,0,0.5)", zIndex: 1050 }}
          onClick={() => setPaymentOverlay(false)}
        >
          <div
            className="d-flex gap-4 flex-wrap justify-content-center"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Online Payment Card */}
            <div
              className="card shadow-sm"
              style={{ width: 220, cursor: "pointer", borderRadius: 16 }}
              onClick={() => handlePaymentSelect("ONLINE")}
            >
              <div className="card-body d-flex flex-column align-items-center justify-content-center py-5">
                <img
                  src="https://d2nahbmqd5vvug.cloudfront.net/nxtmeal/OnlineImage.png"
                  alt="Online Payment"
                  style={{ width: 48, height: 48, objectFit: "contain", marginBottom: 16 }}
                />
                <h5 className="mb-0 fw-bold text-center">Online Payment</h5>
              </div>
            </div>

            {/* Cash Payment Card */}
            <div
              className="card shadow-sm"
              style={{ width: 220, cursor: "pointer", borderRadius: 16 }}
              onClick={() => handlePaymentSelect("CASH")}
            >
              <div className="card-body d-flex flex-column align-items-center justify-content-center py-5">
                <img
                  src="https://d2nahbmqd5vvug.cloudfront.net/nxtmeal/CashImage.png"
                  alt="Cash Payment"
                  style={{ width: 48, height: 48, objectFit: "contain", marginBottom: 16 }}
                />
                <h5 className="mb-0 fw-bold text-center">Cash Payment</h5>
              </div>
            </div>

            {/* Sodexo Payment Card */}
            <div
              className="card shadow-sm"
              style={{ width: 220, cursor: "pointer", borderRadius: 16 }}
              onClick={() => handlePaymentSelect("SODEXO")}
            >
               <div className="card-body d-flex flex-column align-items-center justify-content-center py-5">
                <img
                  src="https://d2nahbmqd5vvug.cloudfront.net/nxtmeal/mealPass.png"
                  alt="Cash Payment"
                  style={{ width: 48, height: 48, objectFit: "contain", marginBottom: 16 }}
                />
                <h5 className="mb-0 fw-bold text-center">Sodexo</h5>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Modal */}
      {confirmModal && (
        <div
          className="position-fixed top-0 start-0 w-100 h-100 d-flex justify-content-center align-items-center"
          style={{ backgroundColor: "rgba(0,0,0,0.45)", zIndex: 1060 }}
          onClick={() => setConfirmModal(null)}
        >
          <div
            className="bg-white rounded-4 shadow-lg p-4"
            style={{ width: 360, maxWidth: "90vw" }}
            onClick={(e) => e.stopPropagation()}
          >
            <h5 className="fw-bold mb-2">{confirmModal.title}</h5>
            <p className="text-muted mb-4" style={{ fontSize: "0.95rem", whiteSpace: "pre-wrap" }}>{confirmModal.message}</p>
            <div className="d-flex justify-content-end gap-2">
              {!confirmModal.hideCancel && (
                <button
                  className="btn btn-outline-secondary"
                  style={{ borderRadius: 10, padding: "6px 20px" }}
                  onClick={() => setConfirmModal(null)}
                >
                  Cancel
                </button>
              )}
              <button
                className={`btn fw-semibold ${confirmModal.hideCancel ? "btn-primary" : "btn-success"}`}
                style={{ borderRadius: 10, padding: "6px 20px" }}
                onClick={confirmModal.onConfirm}
              >
                {confirmModal.hideCancel ? "OK" : "Confirm"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ItemsPanel;
